import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { initiatePayment, getPackageAmount } from '../utils/razorpay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuickPayButton = ({ packageType, packageName, price }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customerData, setCustomerData] = useState({
    fullName: '',
    phone: '',
    gym: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerData(prev => ({ ...prev, [name]: value }));
  };

  const handlePayNow = async () => {
    if (!customerData.fullName || !customerData.phone || !customerData.gym) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsProcessing(true);

    try {
      const amount = getPackageAmount(packageType);

      // Create payment order
      const orderResponse = await axios.post(`${BACKEND_URL}/api/payment/create-order`, {
        amount: amount,
        currency: 'INR',
        receipt: `FITFRAME_QUICK_${Date.now()}`,
        notes: {
          package: packageType,
          packageName: packageName,
          customerName: customerData.fullName,
          gym: customerData.gym
        }
      });

      if (!orderResponse.data.success) {
        throw new Error('Failed to create payment order');
      }

      // Initiate payment
      await initiatePayment({
        amount: amount,
        orderId: orderResponse.data.order_id,
        customerName: customerData.fullName,
        customerPhone: customerData.phone,
        customerEmail: '',
        onSuccess: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${BACKEND_URL}/api/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              // Create booking after successful payment
              await axios.post(`${BACKEND_URL}/api/bookings/`, {
                fullName: customerData.fullName,
                phone: customerData.phone,
                gym: customerData.gym,
                package: packageType,
                date: new Date().toISOString().split('T')[0],
                message: `Quick payment for ${packageName}`,
                paymentStatus: 'paid',
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: amount
              });

              toast.success('🎉 Payment successful! Your package is confirmed. We\'ll contact you soon to schedule the shoot!');
              setIsOpen(false);
              setCustomerData({ fullName: '', phone: '', gym: '' });
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment completed but processing failed. Please contact support.');
          } finally {
            setIsProcessing(false);
          }
        },
        onFailure: (error) => {
          console.error('Payment failed:', error);
          toast.error('Payment failed or cancelled. Please try again.');
          setIsProcessing(false);
        }
      });
    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Button 
        onClick={() => setIsOpen(true)}
        className="w-full bg-[#e8ff47] text-[#0d0d0d] hover:opacity-90 hover:scale-105 rounded-full py-6 font-['DM_Sans'] font-semibold uppercase transition-all"
      >
        Pay Now - {price}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6]">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-[#e8ff47] text-3xl">
              {packageName} Package
            </DialogTitle>
            <DialogDescription className="font-['DM_Sans'] text-[#888680]">
              Complete payment of {price} to confirm your booking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="fullName" className="font-['DM_Sans'] text-[#dfddd6]">
                Full Name *
              </Label>
              <Input
                id="fullName"
                name="fullName"
                value={customerData.fullName}
                onChange={handleInputChange}
                className="bg-[#0d0d0d] border-[#3f4816] text-[#dfddd6] mt-2"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="font-['DM_Sans'] text-[#dfddd6]">
                Phone Number *
              </Label>
              <Input
                id="phone"
                name="phone"
                value={customerData.phone}
                onChange={handleInputChange}
                className="bg-[#0d0d0d] border-[#3f4816] text-[#dfddd6] mt-2"
                placeholder="Enter your phone number"
              />
            </div>

            <div>
              <Label htmlFor="gym" className="font-['DM_Sans'] text-[#dfddd6]">
                Which Gym? *
              </Label>
              <Input
                id="gym"
                name="gym"
                value={customerData.gym}
                onChange={handleInputChange}
                className="bg-[#0d0d0d] border-[#3f4816] text-[#dfddd6] mt-2"
                placeholder="Enter your gym name"
              />
            </div>

            <Button 
              onClick={handlePayNow}
              disabled={isProcessing}
              className="w-full bg-[#e8ff47] text-[#0d0d0d] hover:opacity-90 rounded-full py-6 font-['DM_Sans'] font-semibold uppercase"
            >
              {isProcessing ? 'Processing...' : `Proceed to Payment`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickPayButton;